import { useState, useEffect } from 'react';
import { isDebugEnabled } from '../utils/debug';

// Debug logs storage - shared with main.tsx
export const debugLogs: string[] = [];
const MAX_LOGS = 100;

export function addLog(message: string) {
  const timestamp = new Date().toLocaleTimeString();
  const logMessage = `[${timestamp}] ${message}`;
  debugLogs.push(logMessage);
  if (debugLogs.length > MAX_LOGS) debugLogs.shift();
}

export default function DebugPanel() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [, forceUpdate] = useState(0);
  const [visible, setVisible] = useState(false);

  // Check visibility on mount and on storage changes
  useEffect(() => {
    const checkVisibility = () => {
      setVisible(isDebugEnabled());
    };

    // Check on mount
    checkVisibility();

    // Listen for storage changes (from Admin.tsx toggle)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'crowdbuy_debug_enabled' || e.key === 'crowdbuy_is_admin') {
        checkVisibility();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also poll periodically in case localStorage was modified in same window
    const interval = setInterval(checkVisibility, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  if (!visible) {
    return null;
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = debugLogs.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    debugLogs.length = 0;
    addLog('Logs cleared');
    forceUpdate((n) => n + 1);
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(0,0,0,0.95)',
        zIndex: 9999,
        borderTop: '2px solid #0f0',
        transition: 'max-height 0.3s ease',
        maxHeight: isExpanded ? '300px' : '36px',
      }}
    >
      {/* Header - clickable to expand/collapse */}
      <div
        onClick={handleToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderBottom: isExpanded ? '1px solid #333' : 'none',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#0f0', fontSize: '12px', fontWeight: 'bold' }}>🔧 Debug</span>
          <span style={{ color: '#666', fontSize: '11px' }}>
            {debugLogs.length} logs {isExpanded ? '▼' : '▲'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={handleCopy}
            style={{
              background: 'transparent',
              border: '1px solid #0f0',
              color: '#0f0',
              fontSize: '11px',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            {copied ? '✓' : '📋'}
          </button>
          <button
            onClick={handleClear}
            style={{
              background: 'transparent',
              border: '1px solid #666',
              color: '#666',
              fontSize: '11px',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            🗑️
          </button>
        </div>
      </div>

      {/* Logs panel */}
      {isExpanded && (
        <div
          id="debug-panel"
          style={{
            maxHeight: '240px',
            overflow: 'auto',
            padding: '4px 12px',
          }}
        >
          {debugLogs.map((log, i) => (
            <div
              key={i}
              style={{
                fontSize: '10px',
                color: '#0f0',
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.4',
              }}
            >
              {log}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}